/**
 * Power Role - Validate Pillar
 *
 * Validação de valores de potência.
 * ZERO dependências de outros pilares - totalmente standalone.
 *
 * @example
 * import { validatePower, isValidPower } from '@attrx/role-morphic/power/validate';
 *
 * validatePower(100);   // { valid: true, errors: [] }
 * validatePower(-5);    // { valid: false, errors: ['Power cannot be negative'] }
 * isValidPower(100);    // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface PowerValidationConfig {
  /** Valor mínimo permitido (default: 0) */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Mensagem de erro para valor abaixo do mínimo */
  minError?: string;
  /** Mensagem de erro para valor acima do máximo */
  maxError?: string;
  /** Permitir valores negativos (default: false) */
  allowNegative?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// =============================================================================
// DEFAULT CONFIG
// =============================================================================

export const POWER_VALIDATION_CONFIG: PowerValidationConfig = {
  min: 0,
  minError: "Power cannot be negative",
  allowNegative: false,
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de potência
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns Resultado da validação com lista de erros
 *
 * @example
 * validatePower(100);                    // { valid: true, errors: [] }
 * validatePower(-5);                     // { valid: false, errors: ['Power cannot be negative'] }
 * validatePower(-5, { allowNegative: true }); // { valid: true, errors: [] }
 * validatePower(1000, { max: 500 });     // { valid: false, errors: ['Value must be at most 500'] }
 */
export function validatePower(
  value: unknown,
  config: PowerValidationConfig = POWER_VALIDATION_CONFIG,
): ValidationResult {
  const errors: string[] = [];

  // Must be a number
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return {
      valid: false,
      errors: ["Value must be a finite number"],
    };
  }

  // Merge with defaults
  const effectiveConfig = { ...POWER_VALIDATION_CONFIG, ...config };

  // Check negative
  if (!effectiveConfig.allowNegative && value < 0) {
    errors.push(effectiveConfig.minError || "Power cannot be negative");
  }

  // Check min (if allowNegative is true and min is set)
  if (
    effectiveConfig.allowNegative &&
    effectiveConfig.min !== undefined &&
    value < effectiveConfig.min
  ) {
    errors.push(effectiveConfig.minError || `Value must be at least ${effectiveConfig.min}`);
  }

  // Check max
  if (effectiveConfig.max !== undefined && value > effectiveConfig.max) {
    errors.push(effectiveConfig.maxError || `Value must be at most ${effectiveConfig.max}`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Verifica se valor de potência é válido (helper booleano)
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidPower(100);  // true
 * isValidPower(-5);   // false
 */
export function isValidPower(
  value: unknown,
  config?: PowerValidationConfig,
): boolean {
  return validatePower(value, config).valid;
}
